/**
 * Full-height tenant shell (Data Plane, …) — matches Network Workbench tab chrome
 * without the build prompt column.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tab } from '@headlessui/react';
import { Database, Settings2, X } from 'lucide-react';
import DataPlaneCatalog from '@stepflow/ui-shared/components/dataPlane/DataPlaneCatalog.jsx';
import TenantBuilderLimitsPanel from '@stepflow/ui-shared/components/workspace/TenantBuilderLimitsPanel.jsx';

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   orgId?: string,
 *   tenantId?: string,
 * }} props
 */
export default function TenantSettingsPanel({
  open,
  onClose,
  orgId,
  tenantId,
}) {
  const [selectedTab, setSelectedTab] = useState(0);

  const subtitle = useMemo(() => {
    const tid = (tenantId || '').trim();
    const oid = (orgId || '').trim();
    if (!tid && !oid) {
      return <span className="text-slate-500">No tenant id in session.</span>;
    }
    if (tid) {
      return (
        <>
          <span className="font-medium text-slate-600">tenant_id</span>{' '}
          <code className="text-slate-800">{tid}</code>
          {oid && oid !== tid ? (
            <span className="mt-1 block text-[10px] font-normal text-slate-400">
              <span className="font-medium text-slate-500">org_id</span>{' '}
              <code className="text-slate-600">{oid}</code>
            </span>
          ) : null}
        </>
      );
    }
    return (
      <>
        <span className="font-medium text-slate-600">org_id</span>{' '}
        <code className="text-slate-800">{oid}</code>
      </>
    );
  }, [orgId, tenantId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-y-0 right-0 z-[140] flex bg-black/30"
      style={{ left: 52 }}
      role="presentation"
    >
      <button
        type="button"
        aria-label="Dismiss"
        className="min-h-0 min-w-[64px] max-w-[40%] flex-1 cursor-default border-0 bg-transparent p-0"
        onClick={onClose}
      />
      <div
        className="flex h-full min-w-0 flex-1 flex-col border-l border-gray-200 bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tenant-settings-title"
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3 sm:px-5">
          <div className="min-w-0">
            <h1
              id="tenant-settings-title"
              className="text-sm font-semibold text-gray-900"
            >
              Tenant
            </h1>
            <div className="mt-1 text-[11px] leading-snug text-slate-600">{subtitle}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
            aria-label="Close"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <Tab.Group
          as="div"
          selectedIndex={selectedTab}
          onChange={setSelectedTab}
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
        >
          <div className="min-w-0 shrink-0 border-b border-gray-200 bg-white px-3 sm:px-4">
            <Tab.List className="-mb-px flex w-full min-w-0 flex-wrap items-center gap-x-1 gap-y-1 overflow-x-auto pb-px sm:gap-x-2">
              <Tab
                className={({ selected }) =>
                  `shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-1.5 text-[13px] font-medium transition-colors ${
                    selected
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                  }`
                }
              >
                <Database className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                Context
              </Tab>
              <Tab
                className={({ selected }) =>
                  `shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-2 py-1.5 text-[13px] font-medium transition-colors ${
                    selected
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
                  }`
                }
              >
                <Settings2 className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
                Settings
              </Tab>
            </Tab.List>
          </div>
          <Tab.Panels className="min-h-0 min-w-0 flex-1 overflow-hidden bg-white">
            <Tab.Panel
              unmount={false}
              className="flex h-full min-h-0 flex-col overflow-hidden outline-none"
            >
              <DataPlaneCatalog
                includeOrg
                showUpload
                listFilter={{}}
                agentNames={[]}
                poolIds={[]}
              />
            </Tab.Panel>
            <Tab.Panel
              unmount={false}
              className="flex h-full min-h-0 flex-col overflow-hidden outline-none"
            >
              <TenantBuilderLimitsPanel tenantId={tenantId} />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    </div>,
    document.body,
  );
}
